import { Radio as RadioIcon, Volume2 } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PttButton, radioLinkText, SpeakerBanner, useRadio } from '@despachador/ui';
import type { RadioClient } from '@despachador/api-client/ptt';
import { api, socket, useSession } from './session';

const KEY = 'admin.radio.on';

interface RadioCtxValue {
  /** The radio is allowed for this user (feature + permission). */
  allowed: boolean;
  /** Radio switched on by the operator (remembered on this device). */
  on: boolean;
  radio: RadioClient | null;
  turnOn: () => Promise<void>;
  turnOff: () => void;
}

const RadioCtx = createContext<RadioCtxValue>({ allowed: false, on: false, radio: null, turnOn: async () => undefined, turnOff: () => undefined });
export const useRadioClient = () => useContext(RadioCtx);

const remembered = () => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};
const remember = (on: boolean) => {
  try {
    localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    // private mode: the operator switches it on again next time
  }
};

/**
 * One radio for the whole panel: the operator keeps listening while working on services or the
 * map. Off until the operator switches it on (the microphone is asked then, not at login).
 */
export function RadioProvider({ children }: { children: ReactNode }) {
  const { can, feature, me } = useSession();
  const allowed = feature('ptt') && can('communication.view');
  const [on, setOn] = useState(() => allowed && remembered());
  const [radio, setRadio] = useState<RadioClient | null>(null);

  useEffect(() => {
    if (!allowed || !on) return;
    let client: RadioClient | null = null;
    let cancelled = false;
    void import('@despachador/api-client/ptt').then(({ RadioClient }) => {
      if (cancelled) return;
      client = new RadioClient({
        socket,
        fetchChannels: () => api.get('/me/ptt/channels'),
        wantMic: can('communication.transmit'),
        storageKey: 'admin.radio.channel',
        userId: me.user.id,
      });
      client.start();
      setRadio(client);
    });
    return () => {
      cancelled = true;
      client?.stop();
      setRadio(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, on]);

  const turnOn = useCallback(async () => {
    remember(true);
    setOn(true);
  }, []);
  const turnOff = useCallback(() => {
    remember(false);
    setOn(false);
  }, []);

  return <RadioCtx.Provider value={{ allowed, on: allowed && on, radio, turnOn, turnOff }}>{children}</RadioCtx.Provider>;
}

/** Compact radio in the panel layout: who talks + talk button, on every page. */
export function RadioBar() {
  const { on, radio } = useRadioClient();
  const s = useRadio(radio);
  if (!on || !radio || !s || s.link.phase === 'DISCONNECTED') return null;
  const live = Object.values(s.channels).find((c) => c.speaker && c.speaker.userId !== s.userId) ?? null;
  const active = s.activeChannelId ? s.channels[s.activeChannelId] : null;
  const link = radioLinkText(s);
  return (
    <div className="radio-bar" aria-label="Radio">
      <Link to="/radio" className="radio-bar-info">
        {live ? (
          <SpeakerBanner speaker={live.speaker} channelName={live.name} compact />
        ) : (
          <span className="row small">
            <RadioIcon size={16} aria-hidden /> {active?.name ?? 'Radio'} · <span className={link.tone === 'ok' ? '' : 'warn-text'}>{link.text}</span>
          </span>
        )}
      </Link>
      {s.audioBlocked && (
        <button type="button" className="btn sm" onClick={() => void radio.enableAudio()}>
          <Volume2 size={14} aria-hidden /> Activar sonido
        </button>
      )}
      {active?.canTransmit && <PttButton client={radio} snapshot={s} size="dock" />}
    </div>
  );
}
